import { useState, useEffect } from 'react';
import { useRole } from '../App';
import { supabase } from '../utils/supabase';
import { getOperators } from '../utils/storage';
import { CATEGORIES, TIERS, scoreUnit, getTier } from '../utils/scoring';

export default function OperatorDashboard() {
  const { userRecord } = useRole();
  const [operators, setOperators] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tiers');
  const [compType, setCompType] = useState('menu');
  const [compNotes, setCompNotes] = useState('');

  useEffect(() => { loadAll(); }, [userRecord]);

  const loadAll = async () => {
    const all = await getOperators();
    const accessible = userRecord?.operator_ids?.length > 0 ? all.filter(o => userRecord.operator_ids.includes(o.id)) : [];
    setOperators(accessible);

    if (accessible.length > 0) {
      const opIds = accessible.map(o => o.id);
      const [compRes, actRes] = await Promise.all([
        supabase.from('bc_compliance').select('*').in('operator_id', opIds).order('updated_at', { ascending: false }),
        supabase.from('bc_activations').select('*').in('operator_id', opIds).order('created_at', { ascending: false }),
      ]);
      setCompliance(compRes.data || []);
      setActivations(actRes.data || []);
    }
    setLoading(false);
  };

  const allUnits = operators.flatMap(o => (o.data?.units || []).map(u => ({ ...u, operatorName: o.data?.operatorName || o.operator_name, operatorId: o.id })));
  const scored = allUnits.filter(u => +u.totalSales > 0).map(u => ({ ...u, results: scoreUnit(u) })).filter(u => u.results);

  const submitCompliance = async () => {
    if (!operators[0]?.id) return;
    const unitName = scored[0]?.name || 'Unit 1';
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('bc_compliance').insert({
      operator_id: operators[0].id, unit_name: unitName,
      data: { type: compType, notes: compNotes, status: 'submitted', submittedBy: user?.email, submittedAt: new Date().toISOString() }
    });
    setCompNotes('');
    loadAll();
  };

  if (loading) return <div className="py-20 text-center text-neutral-500">Loading...</div>;

  const TABS = [
    { id: 'tiers', label: 'My Tiers' },
    { id: 'activations', label: `Activations (${activations.length})` },
    { id: 'compliance', label: `Compliance (${compliance.length})` },
    { id: 'submit', label: 'Submit Data' },
  ];

  return (
    <div className="py-6">
      <div className="mb-6">
        <h1 className="font-display text-2xl text-white">My Beverage Program</h1>
        <p className="text-xs text-neutral-500">{userRecord?.company_name || 'Operator'} · Beverage Collective</p>
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? 'bg-brand-gold/15 text-brand-gold' : 'text-neutral-500 hover:text-neutral-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tiers */}
      {activeTab === 'tiers' && scored.map((u, i) => (
        <div key={i} className="card p-5 mb-4">
          <h2 className="text-sm font-semibold text-white mb-1">{u.name}</h2>
          <p className="text-[10px] text-neutral-500 mb-4">{u.market} · {u.type} · {u.cuisine}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {u.results.map(r => (
              <div key={r.id} className="bg-neutral-800/30 rounded-xl p-4 text-center" style={{ borderTop: `3px solid ${r.tier.color}` }}>
                <p className="text-[10px] text-neutral-500 uppercase">{r.icon} {r.name}</p>
                <p className="text-3xl font-black mt-1" style={{ color: r.tier.color }}>T{r.tier.num}</p>
                <p className="text-xs font-semibold" style={{ color: r.tier.color }}>{r.tier.name}</p>
                <div className="h-1.5 bg-neutral-800 rounded-full mt-2 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${r.finalScore}%`, backgroundColor: r.tier.color }} />
                </div>
                <p className="text-[8px] text-neutral-600 mt-2">{r.tier.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Activations */}
      {activeTab === 'activations' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Your Activations</h2>
          {activations.length > 0 ? (
            <div className="space-y-2">
              {activations.map(a => {
                const cat = CATEGORIES.find(c => c.id === a.data?.category);
                return (
                  <div key={a.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                    <span className="text-lg">{cat?.icon || '📋'}</span>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-white">{a.data?.name}</p>
                      <p className="text-[10px] text-neutral-500">
                        {a.data?.date ? new Date(a.data.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                        {a.data?.supplier ? ` · ${a.data.supplier}` : ''}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded font-medium ${a.data?.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : a.data?.status === 'active' ? 'bg-blue-500/15 text-blue-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {a.data?.status || 'planned'}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-neutral-600 text-xs text-center py-4">No activations scheduled.</p>}
        </div>
      )}

      {/* Compliance */}
      {activeTab === 'compliance' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Compliance Status</h2>
          {compliance.length > 0 ? (
            <div className="space-y-2">
              {compliance.map(c => (
                <div key={c.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-white">{c.unit_name} — {c.data?.type}</p>
                    {c.data?.notes && <p className="text-[10px] text-neutral-500">{c.data.notes}</p>}
                    <p className="text-[9px] text-neutral-700">{new Date(c.updated_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded font-medium ${c.data?.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : c.data?.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {c.data?.status || 'submitted'}
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="text-neutral-600 text-xs text-center py-4">No compliance records.</p>}
        </div>
      )}

      {/* Submit Data — operator self-entry */}
      {activeTab === 'submit' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Submit Compliance Data</h2>
          <p className="text-xs text-neutral-500 mb-4">Upload menus, P-mix reports, activation proof, or education certificates.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={compType} onChange={e => setCompType(e.target.value)} className="input-field text-xs py-2 w-40">
              <option value="menu">Menu Upload</option>
              <option value="pmix">P-Mix Report</option>
              <option value="activation">Activation Proof</option>
              <option value="education">Education Certificate</option>
              <option value="revenue">Revenue Update</option>
              <option value="other">Other</option>
            </select>
            <input value={compNotes} onChange={e => setCompNotes(e.target.value)} placeholder="Description / notes..." className="input-field text-xs py-2 flex-1" />
            <button onClick={submitCompliance} className="btn-primary text-xs py-2">Submit</button>
          </div>
          <p className="text-[9px] text-neutral-700">Submissions are reviewed by the NEG team. You'll see the status update in the Compliance tab.</p>
        </div>
      )}

      {scored.length === 0 && activeTab === 'tiers' && (
        <div className="card p-8 text-center">
          <p className="text-neutral-500">Your program hasn't been scored yet.</p>
          <p className="text-neutral-600 text-xs mt-2">Contact Next Experience Group to get started.</p>
        </div>
      )}
    </div>
  );
}
